'use client'

import { ChevronDownIcon, CircleQuestionMarkIcon, UserIcon } from 'lucide-react'
import Link from 'next/link'
import React from 'react'

import { accountMenuLinkGroup } from './siteLinks'
import { Button } from './ui/Button'
import { cn } from './ui/cn'
import { Dropdown } from './ui/Dropdown'
import { Wrapper } from './ui/Wrapper'

const UserActions = Wrapper

interface SiteUserBarProps {
  className?: string
  isLoggedIn?: boolean
  firstName?: string
  onLogIn?: () => void
  onLogOut?: () => void
}

const brandFamilyLinks: ReadonlyArray<{
  href: string
  logoUrl: string
  label: string
}> = [
  {
    href: 'https://www.shipsticks.com',
    logoUrl: '/brands/shipsticks_graphic_light.svg',
    label: 'ShipSticks',
  },
  {
    href: 'https://www.shipskis.com',
    logoUrl: '/brands/shipskis_graphic_light.svg',
    label: 'ShipSkis',
  },
  {
    href: 'https://www.shipcamps.com',
    logoUrl: '/brands/shipcamps_graphic_light.svg',
    label: 'ShipCamps',
  },
]

/**
 * Ship&Play top utility bar. Brand-family logo row on the left (ShipSticks,
 * ShipSkis, ShipCamps); on md+ a shortcut group on the right (Travel Advisor
 * Registration + FAQ + auth). Auth is a Log In button when signed-out or an
 * account Dropdown when signed-in — mirrors playground's SiteUserBar. On
 * mobile the entire right-side group is hidden and login lives in the
 * NavSheet instead.
 */
export const SiteUserBar = ({
  className,
  isLoggedIn = false,
  firstName,
  onLogIn,
  onLogOut,
}: SiteUserBarProps): React.ReactElement => {
  return (
    <div
      className={cn(
        'snplay:relative snplay:z-[81]',
        'snplay:bg-ui-primary snplay:text-ui-foreground',
        className,
      )}
    >
      <div
        className={cn(
          'snplay-bounds snplay:flex snplay:min-h-12',
          'snplay:items-center snplay:justify-center snplay:text-sm',
          'snplay:md:justify-between',
        )}
      >
        <div className="snplay:flex snplay:items-center snplay:gap-8">
          {brandFamilyLinks.map((brandLink) => (
            <a
              key={brandLink.label}
              href={brandLink.href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'snplay-can-focus snplay:flex snplay:min-h-11 snplay:items-center',
                'snplay:transition-colors snplay:hover:text-primary',
              )}
            >
              <img src={brandLink.logoUrl} alt={brandLink.label} className="snplay:h-7" />
            </a>
          ))}
        </div>
        <UserActions
          className={cn(
            'snplay:hidden snplay:items-center snplay:justify-end',
            'snplay:gap-1 snplay:text-end snplay:md:flex',
          )}
        >
          <Link
            href="/advisor-signup"
            className={cn(
              'snplay-link snplay-can-focus snplay:hidden',
              'snplay:min-h-11 snplay:items-center snplay:px-2',
              'snplay:underline snplay:decoration-primary snplay:underline-offset-2',
              'snplay:hover:text-primary snplay:md:flex',
            )}
          >
            Travel Advisor Registration
          </Link>
          <Button
            variant="ghost"
            className="snplay:px-3 snplay:text-ui-foreground snplay:hover:text-primary"
            aria-label="Help and support"
            to="/faq"
          >
            <CircleQuestionMarkIcon className="snplay:size-5" />
            <span className="snplay:sr-only">FAQ</span>
          </Button>
          {isLoggedIn ? (
            <Dropdown
              renderTrigger={({ isOpen, triggerProps }): React.ReactElement => (
                <Button
                  variant="ghost"
                  className={cn('snplay:px-2 snplay:text-ui-foreground snplay:hover:text-primary')}
                  {...triggerProps}
                >
                  <UserIcon className="snplay:size-5" />
                  {firstName || 'Account'}
                  <ChevronDownIcon
                    className={cn(
                      'snplay:size-4 snplay:transition-transform',
                      isOpen && 'snplay:rotate-180',
                    )}
                  />
                </Button>
              )}
              renderContent={({ close, contentProps }): React.ReactElement => (
                <nav
                  {...contentProps}
                  aria-label="Account"
                  className={cn(
                    'snplay:absolute snplay:right-0 snplay:top-full snplay:z-[90]',
                    'snplay:mt-1 snplay:w-56 snplay:overflow-hidden',
                    'snplay:rounded-2xl snplay:bg-background',
                    'snplay:py-2 snplay:text-left snplay:text-sm',
                    'snplay:text-ui-primary snplay:shadow-lg',
                  )}
                >
                  <ul>
                    {accountMenuLinkGroup.links.map((link) => (
                      <li key={link.label}>
                        <Link
                          href={link.url}
                          target={link.target}
                          onClick={close}
                          className="snplay-can-focus snplay-menu-link"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <button
                        type="button"
                        onClick={(): void => {
                          onLogOut?.()
                          close()
                        }}
                        className="snplay-can-focus snplay-menu-link snplay:w-full"
                      >
                        Log Out
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            />
          ) : (
            <Button
              variant="ghost"
              className="snplay:px-2 snplay:text-ui-foreground snplay:hover:text-primary"
              onClick={onLogIn}
            >
              <UserIcon className="snplay:size-5" />
              Log In
            </Button>
          )}
        </UserActions>
      </div>
    </div>
  )
}
